import { isFeatureFlagEnabled } from '@onesource/components';
import { CommunityOutlined, Exercise } from '@onesource/icons';
import type { TFeedSpaceItem, TSpaceBase, TUserAccessMap } from '@onesource/schemas';
import { SpaceType } from '@onesource/schemas';
import { getSession, useGetAllProfileQuery, useGetFeedSpaces, useGetMySpaces } from '@onesource/services';
import { useMemo } from 'react';
import { RiBookOpenLine, RiChat3Line } from 'react-icons/ri';
import { ALL_APPS } from 'src/config';
import { EFeatureFlagEnum } from 'src/hooks/use-feature-flag-utils';
import { hasAppAccess, hasCommunityAccess, hasSpaceAccess } from 'src/utils/app-access';

import { getBottomSectionItems, socialLinks, topSectionItems } from './sidebar-data';
import type { SidebarItem, SidebarSection, SidebarSubItem, SocialLink } from './types';
import { useClassicAreaLink } from './use-classic-area-link';

interface UseSidebarDataReturn {
  sidebarData: SidebarSection[];
  socialLinks: SocialLink[];
  isLoading: boolean;
}

// Program apps (FIT/RYC/ADDON/…) render as app items, never as space items.
const APP_CARD_IDS = new Set<string>(ALL_APPS.map(app => app.id));

/**
 * Build the nested sub-space items shown under a container space "folder".
 * Each active sub-space links to the route matching its type. Sub-space types
 * without a dedicated route yet (e.g. chat) are skipped.
 */
const buildSubSpaceItems = (space: TSpaceBase): SidebarSubItem[] => {
  const children = Array.isArray(space.children) ? space.children : [];

  return children.flatMap<SidebarSubItem>(child => {
    switch (child.spaceType) {
      case SpaceType.FEED: {
        return [
          { id: child.id, label: child.name, path: `/community-hub/spaces/${child.id}/community`, icon: RiChat3Line },
        ];
      }
      case SpaceType.CONTENT: {
        return [
          {
            id: child.id,
            label: child.name,
            path: `/community-hub/spaces/${space.id}/modules/${child.id}`,
            icon: RiBookOpenLine,
          },
        ];
      }
      default: {
        return [];
      }
    }
  });
};

/**
 * Build sidebar items from the space catalog. Only community spaces (access entries
 * that aren't program apps) the user has active access (`sub.active`) for are shown.
 * When `grouped` is on, container spaces render as collapsible folders with their
 * sub-spaces nested underneath; when off they are flat rows (no nested folders).
 */
const buildSpaceItems = (
  spaces: TSpaceBase[] | undefined,
  access: TUserAccessMap | undefined,
  grouped: boolean,
): SidebarSubItem[] => {
  if (!Array.isArray(spaces) || spaces.length === 0) {
    return [];
  }

  return spaces
    .filter(space => !!space.app && !APP_CARD_IDS.has(space.app) && hasSpaceAccess(access, space.app))
    .map(space => {
      const isSingleLesson = space.spaceType === SpaceType.SINGLE_LESSON;
      // Single-lesson spaces have a dedicated leaf route; containers land on the space home.
      const path = isSingleLesson ? `/community-hub/spaces/${space.id}/lesson` : `/community-hub/spaces/${space.id}`;
      const children = !grouped || isSingleLesson ? [] : buildSubSpaceItems(space);

      return {
        id: space.id,
        label: space.name,
        path,
        // Container spaces render as folders (the open/closed folder tree icon
        // conveys that); only leaf single-lesson spaces need a leading icon.
        icon: isSingleLesson ? RiBookOpenLine : undefined,
        isLocked: false,
        ...(children.length > 0 ? { children } : {}),
      };
    });
};

/**
 * Resolve each FEED sub-space's parent container from the space catalog so the
 * Community menu can group feed spaces under their parent (the feed-spaces
 * endpoint itself doesn't carry parent info).
 */
const buildFeedParentMap = (spaces: TSpaceBase[] | undefined): Map<string, { id: string; name: string }> => {
  const parentByFeedId = new Map<string, { id: string; name: string }>();
  if (!Array.isArray(spaces)) {
    return parentByFeedId;
  }

  for (const space of spaces) {
    for (const child of space.children ?? []) {
      if (child.spaceType === SpaceType.FEED) {
        parentByFeedId.set(child.id, { id: space.id, name: space.name });
      }
    }
  }

  return parentByFeedId;
};

/**
 * Build Community menu items from FEED sub-spaces. When `grouped` is on they are
 * nested under their parent container as collapsible folders (feeds whose parent
 * can't be resolved fall back to flat items); when off every feed renders as a
 * direct, flat row.
 * hasAccess is returned by the backend (getFeedSpaces endpoint) using user_access.
 */
const buildCommunityItems = (
  feedSpaces: TFeedSpaceItem[] | undefined,
  spaces: TSpaceBase[] | undefined,
  showLocked: boolean,
  grouped: boolean,
): SidebarSubItem[] => {
  if (!Array.isArray(feedSpaces) || feedSpaces.length === 0) {
    return [];
  }

  const filtered = showLocked ? feedSpaces : feedSpaces.filter(s => s.hasAccess);

  const toFeedItem = (space: TFeedSpaceItem): SidebarSubItem => ({
    id: `community-${space.id}`,
    label: space.name,
    path: `/community-hub/spaces/${space.id}/community`,
    icon: RiChat3Line,
    isLocked: !space.hasAccess,
  });

  // Grouping off: flat list — every feed space as a direct row.
  if (!grouped) {
    return filtered.map(toFeedItem);
  }

  const parentByFeedId = buildFeedParentMap(spaces);

  // Preserve first-seen order of parent containers; collect each parent's feeds.
  const groups = new Map<string, { name: string; feeds: SidebarSubItem[] }>();
  const ungrouped: SidebarSubItem[] = [];

  for (const space of filtered) {
    const feedItem = toFeedItem(space);

    const parent = parentByFeedId.get(space.id);
    if (!parent) {
      ungrouped.push(feedItem);
      continue;
    }

    const group = groups.get(parent.id);
    if (group) {
      group.feeds.push(feedItem);
    } else {
      groups.set(parent.id, { name: parent.name, feeds: [feedItem] });
    }
  }

  const groupedItems: SidebarSubItem[] = [...groups.entries()].map(([parentId, group]) => ({
    // Group header is a non-navigable folder (the user may not have program access to the parent).
    id: `community-container-${parentId}`,
    label: group.name,
    children: group.feeds,
  }));

  return [...groupedItems, ...ungrouped];
};

/**
 * Depth-first lookup of the first navigable path in a sub-item tree — used to
 * point the top-level menu entry at a real destination when its first child is
 * a non-navigable folder.
 */
const findFirstPath = (items: SidebarSubItem[]): string | undefined => {
  for (const item of items) {
    if (item.path) {
      return item.path;
    }
    const childPath = item.children && findFirstPath(item.children);
    if (childPath) {
      return childPath;
    }
  }
  return undefined;
};

/**
 * Build app items from access (FIT, RYC, etc.)
 * Only shows apps that have a URL and the user has permissions for that app.
 */
const buildAppItems = (access: TUserAccessMap | undefined): SidebarSubItem[] => {
  if (!access) {
    return [];
  }

  return ALL_APPS.filter(app => {
    if (!app.url) {
      return false;
    }
    return hasAppAccess(access, app.id);
  }).map(app => ({
    id: app.id,
    label: app.name.subscribed,
    externalApp: {
      appId: app.id,
      url: `${app.url}`,
    },
  }));
};

export const useSidebarData = (): UseSidebarDataReturn => {
  const token = getSession();
  const { data: profileData, isLoading } = useGetAllProfileQuery({ token });
  const { data: feedSpaces } = useGetFeedSpaces();
  const { data: mySpaces } = useGetMySpaces();
  const { handleClassicPanelClick } = useClassicAreaLink();

  const sidebarData = useMemo<SidebarSection[]>(() => {
    const showLockedSpaces = isFeatureFlagEnabled(EFeatureFlagEnum.SHOW_LOCKED_SPACES);
    // Grouped folders (containers/feeds nested under their parent) are part of the
    // new collapsible-sidebar experience. When off, items render as a flat list.
    const groupItems = isFeatureFlagEnabled(EFeatureFlagEnum.SHOW_COLLAPSIBLE_SIDEBAR);

    // Build app items from access (FIT, RYC, etc.)
    const appItems = buildAppItems(profileData?.access);

    // Build space items from the space catalog, gated by `sub.active` in profile.access
    const spaceItems = buildSpaceItems(mySpaces, profileData?.access, groupItems);

    const allChildren = [...appItems, ...spaceItems];

    const spacesItem: SidebarItem = {
      id: 'programs',
      label: 'Programs',
      icon: Exercise,
      path: '/apps', // Clicking on "Programs" parent navigates to apps page
      children: allChildren,
    };

    // Main section items
    const mainItems: SidebarItem[] = [];

    mainItems.push(spacesItem);

    // Community menu uses feed spaces — FEED sub-spaces within container spaces.
    // hasAccess is computed by the backend using user_access. Only surface the
    // menu when the user actually has access to a community space (mirrors the
    // community route guard).
    const communityChildren = buildCommunityItems(feedSpaces, mySpaces, showLockedSpaces, groupItems);
    if (communityChildren.length > 0 && hasCommunityAccess(feedSpaces)) {
      const communityItem: SidebarItem = {
        id: 'community',
        label: 'Community',
        icon: CommunityOutlined,
        path: findFirstPath(communityChildren),
        children: communityChildren,
      };
      mainItems.push(communityItem);
    }

    return [
      {
        id: 'top',
        items: topSectionItems,
        showDivider: true,
      },
      {
        id: 'main',
        items: mainItems,
        showDivider: true,
      },
      {
        id: 'bottom',
        items: getBottomSectionItems(() => handleClassicPanelClick()),
      },
    ];
  }, [profileData, feedSpaces, mySpaces, handleClassicPanelClick]);

  return {
    sidebarData,
    socialLinks,
    isLoading,
  };
};
